<script lang="ts">
  import "../../app.css"
  import { writable } from "svelte/store"
  import { setContext } from "svelte"
  import { GraduationCap, School } from "lucide-svelte"
  import { Tabs, TabsList, TabsTrigger } from "$lib/components/ui/tabs/index"

  interface Props {
    children?: import("svelte").Snippet
  }

  let { children }: Props = $props()

  // Audience segmentation store
  const audience = writable<"students" | "universities">("students")

  // Make the audience store available to child components
  setContext("audience", audience)
</script>

<div class="navbar bg-base-100 w-full border-b sticky top-0 z-50 px-4 md:px-8">
  <div class="flex-1">
    <a class="btn btn-ghost normal-case" href="/">
      <img
        src="/images/atr-logo.png"
        alt="Ace The Round Logo"
        class="h-10 w-auto"
      />
    </a>
  </div>

  <!-- Audience Segmentation Tabs -->
  <Tabs
    value={$audience}
    onValueChange={(value) =>
      audience.set(value as "students" | "universities")}
    class="absolute left-1/2 transform -translate-x-1/2 hidden md:block"
  >
    <TabsList class="grid w-64 grid-cols-2 bg-base-200 p-1 rounded-full">
      {#if $audience === "students"}
        <TabsTrigger
          value="students"
          class="rounded-full px-4 py-1 text-sm font-medium bg-primary text-white font-semibold"
        >
          <GraduationCap class="w-4 h-4 mr-2 text-white" />
          Student
        </TabsTrigger>
        <TabsTrigger
          value="universities"
          class="rounded-full px-4 py-1 text-sm font-medium text-neutral"
        >
          <School class="w-4 h-4 mr-2" />
          University
        </TabsTrigger>
      {:else}
        <TabsTrigger
          value="students"
          class="rounded-full px-4 py-1 text-sm font-medium text-neutral"
        >
          <GraduationCap class="w-4 h-4 mr-2" />
          Student
        </TabsTrigger>
        <TabsTrigger
          value="universities"
          class="rounded-full px-4 py-1 text-sm font-medium bg-primary text-white font-semibold"
        >
          <School class="w-4 h-4 mr-2 text-white" />
          University
        </TabsTrigger>
      {/if}
    </TabsList>
  </Tabs>

  <div class="flex-none">
    <ul
      class="menu menu-horizontal px-1 hidden sm:flex font-bold text-lg gap-1"
    >
      <li>
        <a href="/#how-it-works">How It Works</a>
      </li>
      <li>
        <a href="/pricing">Pricing</a>
      </li>
      <li>
        <a href="/account" class="border border-primary">Log in</a>
      </li>
    </ul>
    <div class="dropdown dropdown-end sm:hidden">
      <!-- svelte-ignore a11y_label_has_associated_control -->
      <!-- svelte-ignore a11y_no_noninteractive_tabindex -->
      <label tabindex="0" class="btn btn-ghost btn-circle">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          class="h-5 w-5"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          ><path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M4 6h16M4 12h16M4 18h7"
          /></svg
        >
      </label>
      <!-- svelte-ignore a11y_no_noninteractive_tabindex -->
      <ul
        tabindex="0"
        class="menu menu-lg dropdown-content mt-3 z-[1] p-2 shadow bg-base-100 rounded-box w-52 font-bold"
      >
        <li>
          <div class="pb-2 border-b border-base-200">
            <div class="font-semibold mb-1">View As:</div>
            <div class="flex gap-2">
              {#if $audience === "students"}
                <button
                  class="btn btn-sm bg-primary text-white border-transparent"
                  onclick={() => audience.set("students")}
                >
                  <GraduationCap class="w-4 h-4 mr-1" /> Student
                </button>
                <button
                  class="btn btn-sm bg-base-200 text-neutral border-transparent hover:bg-base-300"
                  onclick={() => audience.set("universities")}
                >
                  <School class="w-4 h-4 mr-1" /> University
                </button>
              {:else}
                <button
                  class="btn btn-sm bg-base-200 text-neutral border-transparent hover:bg-base-300"
                  onclick={() => audience.set("students")}
                >
                  <GraduationCap class="w-4 h-4 mr-1" /> Student
                </button>
                <button
                  class="btn btn-sm bg-primary text-white border-transparent"
                  onclick={() => audience.set("universities")}
                >
                  <School class="w-4 h-4 mr-1" /> University
                </button>
              {/if}
            </div>
          </div>
        </li>
        <li><a href="/#how-it-works">How It Works</a></li>
        <li><a href="/pricing">Pricing</a></li>
        <li>
          <a href="/account" class="text-primary font-semibold">Log in</a>
        </li>
      </ul>
    </div>
  </div>
</div>

<div class="">
  {@render children?.()}
</div>

<!-- Spacer grows so the footer can be at bottom on short pages -->
<div class="flex-grow"></div>

<!-- Footer with subtle background pattern -->
<div class="relative overflow-hidden">
  <!-- Subtle background pattern -->
  <div class="absolute inset-0 opacity-5">
    <div
      class="absolute inset-0"
      style="background-image: radial-gradient(circle, var(--color-neutral) 1px, transparent 1px); background-size: 20px 20px;"
    ></div>
  </div>

  <div class="border-t max-w-[1000px] mx-auto relative z-10"></div>
  <footer
    class="footer p-10 gap-x-24 lg:gap-x-32 xl:gap-x-48 place-content-center text-base relative z-10"
  >
    <nav>
      <span class="footer-title opacity-80">Explore</span>
      <a class="link link-hover mb-1" href="/">Overview</a>
      <a class="link link-hover my-1" href="/#how-it-works">How It Works</a>
      <a class="link link-hover my-1" href="/pricing">Pricing</a>
      <a class="link link-hover my-1" href="/#faq">FAQ</a>
      <a class="link link-hover my-1" href="/contact_us">Contact Us</a>
    </nav>
    <nav>
      <span class="footer-title opacity-80">Legal</span>
      <a class="link link-hover mb-1" href="/terms">Terms of Service</a>
      <a class="link link-hover my-1" href="/privacy">Privacy Policy</a>
      <a class="link link-hover my-1" href="/cookies">Cookie Policy</a>
    </nav>
    <nav>
      <span class="footer-title opacity-80">Connect</span>
      <a class="link link-hover mb-1" href="/contact_us">Contact Us</a>
      <a class="link link-hover my-1" href="/support">Support</a>
      <a
        class="link link-hover my-1"
        href="https://linkedin.com"
        target="_blank"
        rel="noopener noreferrer">LinkedIn</a
      >
      <a
        class="link link-hover my-1"
        href="https://twitter.com"
        target="_blank"
        rel="noopener noreferrer">Twitter</a
      >
    </nav>
    <aside>
      <span class="footer-title opacity-80">About Us</span>
      <a class="link link-hover max-w-[260px]" href="/">
        <img
          alt="Ace The Round Logo"
          src="/images/atr-logo.png"
          class="w-[80px] h-auto mb-3"
        />
        Your AI-powered interview coach for landing top finance roles. Master technical
        questions, behavioral interviews, and market knowledge.
      </a>
    </aside>
  </footer>
  <div class="mt-8 text-center text-gray-600 pb-8 relative z-10">
    <p>&copy; {new Date().getFullYear()} Ace The Round. All rights reserved.</p>
  </div>
</div>
